import {Card, Container, Group, Menu, rem, Space, Tabs, Text, Title, UnstyledButton} from "@mantine/core";
import {useLocation, useNavigate} from "react-router-dom";
import {PropsWithChildren, useCallback} from "react";
import {
  RouteEntity,
  SiteAssignmentType,
  siteAssignmentTypes,
  TransportCategory,
  transportCategory
} from "@ots-frontend/ots-data";
import {RouteDetails} from "./RouteDetails";
import {createContentStyles} from "../../../theme/useContentStyles";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {RouteAction, RouteActionMenuDropdown} from "../../menu/RouteActionMenu/RouteActionMenu";

const useStyles = createContentStyles((theme) => ({
  mainSection: {
    paddingBottom: theme.spacing.sm
  }
}));

export type RouteProfileProps = PropsWithChildren<{
  route: RouteEntity
}>

const tabs = [
  {
    key: "details",
    value: "Details"
  },
];

export const RouteProfile = (props: RouteProfileProps) => {
  const {route} = props;

  const {classes} = useStyles();

  const navigate = useNavigate();
  const {state} = useLocation();
  const {t} = useTranslation()

  const onEditSiteAssignmentClick = useCallback((type: SiteAssignmentType, category: TransportCategory) => {
    const pathPart = type === siteAssignmentTypes.company ? "company" : "site"
    navigate(`/site-assignments/${pathPart}/bulk-form`, {
      state: {
        type: type,
        formDefaults: {
          entityIds: [route.id],
          entityType: "ROUTE",
          category: category,
        }
      }
    })
  }, [navigate, route.id])

  const onActionClick = useCallback((action: RouteAction) => {
    if (action === "EDIT_WATER_DEL_SITE_ASSIGNMENT") {
      onEditSiteAssignmentClick(siteAssignmentTypes.deliverySite, transportCategory.water)
    } else if (action === "EDIT_WATER_COMPANY_SITE_ASSIGNMENT") {
      onEditSiteAssignmentClick(siteAssignmentTypes.company, transportCategory.water)
    } else if (action === "EDIT_OIL_DEL_SITE_ASSIGNMENT") {
      onEditSiteAssignmentClick(siteAssignmentTypes.deliverySite, transportCategory.oil)
    } else if (action === "EDIT_OIL_COMPANY_SITE_ASSIGNMENT") {
      onEditSiteAssignmentClick(siteAssignmentTypes.company, transportCategory.oil)
    }
  }, [navigate, onEditSiteAssignmentClick, route.entityType, route.id])

  const items = tabs.map((tab) => (
    <Tabs.Tab value={tab.key} key={tab.key}>
      {tab.value}
    </Tabs.Tab>
  ));

  return (
    <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
      <Container fluid={true} className={classes.mainSection}>
        <Group position="apart">
          <Title order={4}>Route: {route.name}</Title>

          <Menu
            width={260}
            position="bottom-end"
            transitionProps={{transition: "pop-top-right"}}
            withinPortal
          >
            <Menu.Target>
              <UnstyledButton>
                <Group spacing={7}>
                  <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                    {t("common.action.plural", "Actions")}
                  </Text>
                  <IconChevronDown size={rem(12)} stroke={1.5}/>
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <RouteActionMenuDropdown route={route} onActionClick={onActionClick}/>
            </Menu.Dropdown>
          </Menu>

        </Group>
      </Container>

      <Card.Section>
        <Container fluid={true}>
          <Tabs
            defaultValue={state?.tabValue ?? "details"}
            onTabChange={(value) => navigate(`?id=${route.id}`, {state: {tabValue: value}})}
          >
            <Tabs.List>{items}</Tabs.List>
            <Space h="md"/>
            <Tabs.Panel value="details">
              <RouteDetails route={route}/>
            </Tabs.Panel>
          </Tabs>
        </Container>
      </Card.Section>
    </Card>
  );
};
